import { saveAs } from 'file-saver'
import { VolumePricingMode, ImportTabStatus } from '@/utils/Enumerations'
import i18n from '@/i18n'

async function sha256 (text) {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export function buildScalingExportData ({
  account,
  logLengths,
  lengthGroups,
  diameterGroups,
  speciesGroups,
  rateSheetDefinitions,
  allSpecies
}) {
  const products = {
    scalingProducts: (account.accountProducts?.filter(p => p.isScalable) ?? account.scalingProducts ?? []).map(p => p.name ?? (typeof p.product === 'object' ? p.product?.name : p.product)),
    fallbackProduct: account.fallbackProduct?.name || account.fallbackProduct?.product || account.fallbackProduct || null,
    fallbackExpands: account.fallbackExpands
  }

  const lengths = (logLengths || []).map(l => l.length)

  const lengthGroupsData = (lengthGroups || []).map(lg => ({
    label: lg.label,
    logLengths: (lg.logLengths || []).map(l => l.length)
  }))

  const diameterGroupsData = (diameterGroups || []).map(dg => ({
    label: dg.label,
    lowerBound: dg.lowerBound,
    upperBound: dg.upperBound
  }))

  const speciesGroupsData = (speciesGroups || []).map(sg => ({
    label: sg.label,
    species: (sg.species || []).map(s => resolveSpeciesName(s, allSpecies)),
    lengthGroups: (sg.lengthGroups || []).map(lg => lg.label),
    diameterGroups: (sg.diameterGroups || []).map(dg => dg.label)
  }))

  const rateSheetDefinitionsData = (rateSheetDefinitions || []).map(rsd => ({
    name: rsd.name,
    rates: (rsd.rates || []).map(vr => ({
      effectiveDate: vr.effectiveDate,
      lines: (vr.rateSheetDefinitionLines || []).map(line => ({
        rate: line.rate,
        speciesGroupLabel: line.speciesGroup?.label || line.speciesGroupLabel || null,
        lengthGroupLabel: line.lengthGroup?.label || line.lengthGroupLabel || null,
        diameterGroupLabel: line.diameterGroup?.label || line.diameterGroupLabel || null,
        gradeName: line.grade?.name || line.gradeName || null,
        endUseSortName: line.endUseSort?.name || line.endUseSortName || null
      }))
    }))
  }))

  return {
    products,
    logLengths: lengths,
    lengthGroups: lengthGroupsData,
    diameterGroups: diameterGroupsData,
    speciesGroups: speciesGroupsData,
    rateSheetDefinitions: rateSheetDefinitionsData,
    volumePricingMode: account.volumePricingMode ?? VolumePricingMode.NotSet.value
  }
}

function resolveSpeciesName (species, allSpecies) {
  if (species.commonName) return species.commonName
  if (species.speciesId && allSpecies?.length > 0) {
    const found = allSpecies.find(s => s.speciesId === species.speciesId)
    if (found) return found.commonName
  }
  return species.name || species.commonName || ''
}

export async function exportScalingSpecs ({
  account,
  logLengths,
  lengthGroups,
  diameterGroups,
  speciesGroups,
  rateSheetDefinitions,
  allSpecies
}) {
  const data = buildScalingExportData({
    account,
    logLengths,
    lengthGroups,
    diameterGroups,
    speciesGroups,
    rateSheetDefinitions,
    allSpecies
  })

  const dataJson = JSON.stringify(data)
  const checksum = await sha256(dataJson)

  const payload = {
    exportedAt: new Date().toISOString(),
    accountName: account.name,
    checksum,
    data
  }

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const safeName = (account.name || i18n.t('account')).replace(/[^a-zA-Z0-9_-]/g, '_')
  saveAs(blob, `${safeName}_ScalingSpecs.json`)
}

export async function parseAndVerifyImportFile (file) {
  let text
  try {
    text = await file.text()
  } catch {
    return { valid: false, error: 'scalingImportParseError' }
  }

  let parsed
  try {
    parsed = JSON.parse(text)
  } catch {
    return { valid: false, error: 'scalingImportParseError' }
  }

  if (!parsed.data) {
    return { valid: false, error: 'scalingImportNoData' }
  }

  // Verify checksum
  const dataJson = JSON.stringify(parsed.data)
  const computedChecksum = await sha256(dataJson)
  if (computedChecksum !== parsed.checksum) {
    return { valid: false, error: 'scalingIntegrityError' }
  }

  return {
    valid: true,
    data: parsed.data,
    accountName: parsed.accountName,
    exportedAt: parsed.exportedAt
  }
}

export async function importScalingSpecs ({
  data,
  account,
  allProducts,
  allSpecies,
  allGrades,
  allEndUseSorts,
  actions
}) {
  const accountId = account.accountId
  const results = []

  const tabOrder = ['products', 'logLengths', 'lengthGroups', 'diameterGroups', 'speciesGroups', 'rateSheetDefinitions']
  const tabNames = {
    products: i18n.t('product'),
    logLengths: i18n.t('length'),
    lengthGroups: i18n.t('lengthGroups'),
    diameterGroups: i18n.t('diameterGroups'),
    speciesGroups: i18n.t('speciesGroups'),
    rateSheetDefinitions: i18n.t('rates')
  }

  const succeeded = new Set()
  const createdLengthGroups = []
  const createdDiameterGroups = []
  const createdSpeciesGroups = []

  for (const tab of tabOrder) {
    const tabName = tabNames[tab] || tab

    try {
      switch (tab) {
        case 'products': {
          await importProducts({
            data, account, accountId, allProducts, actions, results, tabName
          })
          if (results[results.length - 1]?.status === ImportTabStatus.Success.value) succeeded.add(tab)
          break
        }
        case 'logLengths': {
          await importLogLengths({
            data, accountId, actions, results, tabName
          })
          const lastStatus = results[results.length - 1]?.status
          if (lastStatus === ImportTabStatus.Success.value || lastStatus === ImportTabStatus.Skipped.value) succeeded.add(tab)
          break
        }
        case 'lengthGroups': {
          if (!succeeded.has('logLengths') && (data.lengthGroups?.length > 0)) {
            results.push({ tab: tabName, status: ImportTabStatus.SkippedPrerequisite.value, message: i18n.t('scalingTabSkippedPrerequisite') })
            break
          }
          await importLengthGroups({
            data, accountId, actions, results, tabName, createdLengthGroups
          })
          const lastStatus = results[results.length - 1]?.status
          if (lastStatus === ImportTabStatus.Success.value || lastStatus === ImportTabStatus.Skipped.value) succeeded.add(tab)
          break
        }
        case 'diameterGroups': {
          await importDiameterGroups({
            data, accountId, actions, results, tabName, createdDiameterGroups
          })
          const lastStatus = results[results.length - 1]?.status
          if (lastStatus === ImportTabStatus.Success.value || lastStatus === ImportTabStatus.Skipped.value) succeeded.add(tab)
          break
        }
        case 'speciesGroups': {
          const needsLG = data.speciesGroups?.some(sg => sg.lengthGroups?.length > 0)
          const needsDG = data.speciesGroups?.some(sg => sg.diameterGroups?.length > 0)
          if ((needsLG && !succeeded.has('lengthGroups')) || (needsDG && !succeeded.has('diameterGroups'))) {
            results.push({ tab: tabName, status: ImportTabStatus.SkippedPrerequisite.value, message: i18n.t('scalingTabSkippedPrerequisite') })
            break
          }
          await importSpeciesGroups({
            data,
            accountId,
            allSpecies,
            actions,
            results,
            tabName,
            createdLengthGroups,
            createdDiameterGroups,
            createdSpeciesGroups
          })
          const lastStatus = results[results.length - 1]?.status
          if (lastStatus === ImportTabStatus.Success.value || lastStatus === ImportTabStatus.Skipped.value) succeeded.add(tab)
          break
        }
        case 'rateSheetDefinitions': {
          const needsSG = data.rateSheetDefinitions?.some(rsd =>
            rsd.rates?.some(vr => vr.lines?.some(l => l.speciesGroupLabel))
          )
          const needsLG = data.rateSheetDefinitions?.some(rsd =>
            rsd.rates?.some(vr => vr.lines?.some(l => l.lengthGroupLabel))
          )
          const needsDG = data.rateSheetDefinitions?.some(rsd =>
            rsd.rates?.some(vr => vr.lines?.some(l => l.diameterGroupLabel))
          )
          if (
            (needsSG && !succeeded.has('speciesGroups')) ||
            (needsLG && !succeeded.has('lengthGroups')) ||
            (needsDG && !succeeded.has('diameterGroups'))
          ) {
            results.push({ tab: tabName, status: ImportTabStatus.SkippedPrerequisite.value, message: i18n.t('scalingTabSkippedPrerequisite') })
            break
          }
          await importRateSheetDefinitions({
            data,
            account,
            accountId,
            allGrades,
            allEndUseSorts,
            actions,
            results,
            tabName,
            createdSpeciesGroups,
            createdLengthGroups,
            createdDiameterGroups
          })
          const lastStatus = results[results.length - 1]?.status
          if (lastStatus === ImportTabStatus.Success.value || lastStatus === ImportTabStatus.Skipped.value) succeeded.add(tab)
          break
        }
      }
    } catch (err) {
      results.push({ tab: tabName, status: ImportTabStatus.Failed.value, message: `${i18n.t('scalingTabFailed')} ${err.message || ''}` })
    }
  }

  return results
}

async function importProducts ({ data, account, allProducts, actions, results, tabName }) {
  const importProducts = data.products
  if (!importProducts || (!importProducts.scalingProducts?.length && !importProducts.fallbackProduct)) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  if ((account.scalingProducts ?? account.accountProducts?.filter(p => p.isScalable))?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  const missing = []
  const resolvedProducts = []
  for (const name of (importProducts.scalingProducts || [])) {
    const product = allProducts.find(p => p.name === name || p.product === name)
    if (!product) {
      missing.push(name)
    } else {
      resolvedProducts.push(product)
    }
  }

  let resolvedFallback = null
  if (importProducts.fallbackProduct) {
    resolvedFallback = allProducts.find(p => p.name === importProducts.fallbackProduct || p.product === importProducts.fallbackProduct)
    if (!resolvedFallback) missing.push(importProducts.fallbackProduct)
  }

  if (missing.length > 0) {
    results.push({
      tab: tabName,
      status: typeof missing[0] === 'string' ? ImportTabStatus.Failed.value : ImportTabStatus.Skipped.value,
      message: typeof missing[0] === 'string' ? `Products not found: ${missing.join(', ')}` : i18n.t('noXDataToImport', { x: tabName })
    })
    return
  }

  // Merge with the account's existing (non-scale) products — the import only supplies the scale set.
  const existingProducts = (account.accountProducts ?? []).filter(ap => !resolvedProducts.some(rp => rp.productId === ap.productId))
  const mergedProducts = [...existingProducts, ...resolvedProducts]
  const updatedAccount = {
    ...account,
    accountProducts: mergedProducts,
    accountProductIds: mergedProducts.map(p => p.productId),
    fallbackProductId: resolvedFallback?.productId ?? account.fallbackProductId,
    fallbackProduct: resolvedFallback ?? account.fallbackProduct,
    fallbackExpands: importProducts.fallbackExpands ?? true
  }

  await actions.updateAccount(updatedAccount)

  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}

async function importLogLengths ({ data, accountId, actions, results, tabName }) {
  const lengths = data.logLengths
  if (!lengths || lengths.length === 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  const existing = await actions.fetchAccountLogLengths(accountId)
  if (existing?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  await actions.createAccountLogLengths({ accountId, logLengths: lengths })
  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}

async function importLengthGroups ({ data, accountId, actions, results, tabName, createdLengthGroups }) {
  const groups = data.lengthGroups
  if (!groups || groups.length === 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  const existing = await actions.fetchAccountLengthGroups(accountId)
  if (existing?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  const serverLogLengths = await actions.fetchAccountLogLengths(accountId)
  const logLengthByValue = Object.fromEntries(serverLogLengths.map(ll => [ll.length, ll]))

  const missing = []
  for (const group of groups) {
    for (const lengthValue of group.logLengths) {
      if (!logLengthByValue[lengthValue]) {
        missing.push(lengthValue)
      }
    }
  }

  if (missing.length > 0) {
    missing.sort((a, b) => a - b)
    results.push({
      tab: tabName,
      status: ImportTabStatus.Failed.value,
      message: `Missing log lengths: ${[...new Set(missing)].join(', ')}`
    })
    return
  }

  for (const group of groups) {
    const resolvedLogLengths = group.logLengths.map(lengthValue => {
      const serverLL = logLengthByValue[lengthValue]
      return serverLL || { length: lengthValue }
    })
    const logLengthIds = resolvedLogLengths
      .filter(ll => ll.logLengthId)
      .map(ll => ll.logLengthId)

    const created = await actions.createAccountLengthGroup({
      accountId,
      label: group.label,
      logLengthIds,
      logLengths: resolvedLogLengths
    })
    createdLengthGroups.push(created)
  }

  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}

async function importDiameterGroups ({ data, accountId, actions, results, tabName, createdDiameterGroups }) {
  const groups = data.diameterGroups
  if (!groups || groups.length === 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  const existing = await actions.fetchAccountDiameterGroups(accountId)
  if (existing?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  for (const group of groups) {
    const created = await actions.createAccountDiameterGroup({
      accountId,
      label: group.label,
      lowerBound: group.lowerBound,
      upperBound: group.upperBound
    })
    createdDiameterGroups.push(created)
  }

  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}

async function importSpeciesGroups ({
  data, accountId, allSpecies, actions, results, tabName, createdSpeciesGroups
}) {
  const groups = data.speciesGroups
  if (!groups || groups.length === 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  const existing = await actions.fetchAccountSpeciesGroups(accountId)
  if (existing?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  const serverLengthGroups = await actions.fetchAccountLengthGroups(accountId)
  const serverDiameterGroups = await actions.fetchAccountDiameterGroups(accountId)

  // Lookup maps
  const speciesByCommonName = Object.fromEntries(allSpecies.map(s => [s.commonName, s]))
  const lengthGroupByLabel = Object.fromEntries(serverLengthGroups.map(lg => [lg.label, lg]))
  const diameterGroupByLabel = Object.fromEntries(serverDiameterGroups.map(dg => [dg.label, dg]))

  // Pre-validate all references
  const missing = []
  for (const group of groups) {
    for (const speciesName of (group.species || [])) {
      const species = speciesByCommonName[speciesName]
      if (!species) missing.push(`Species: ${speciesName}`)
    }
    for (const lgLabel of (group.lengthGroups || [])) {
      const lg = lengthGroupByLabel[lgLabel]
      if (!lg) missing.push(`Length Group: ${lgLabel}`)
    }
    for (const dgLabel of (group.diameterGroups || [])) {
      const dg = diameterGroupByLabel[dgLabel]
      if (!dg) missing.push(`Diameter Group: ${dgLabel}`)
    }
  }

  if (missing.length > 0) {
    results.push({
      tab: tabName,
      status: ImportTabStatus.Failed.value,
      message: `Missing references: ${[...new Set(missing)].join(', ')}`
    })
    return
  }

  for (const group of groups) {
    const speciesIds = group.species.map(speciesName => {
      const species = speciesByCommonName[speciesName]
      return species.speciesId
    })

    const lengthGroupIds = (group.lengthGroups || []).map(lgLabel => {
      const lg = lengthGroupByLabel[lgLabel]
      return lg.lengthGroupId
    })

    const diameterGroupIds = (group.diameterGroups || []).map(dgLabel => {
      const dg = diameterGroupByLabel[dgLabel]
      return dg.diameterGroupId
    })

    const created = await actions.createAccountSpeciesGroup({
      accountId,
      label: group.label,
      speciesIds,
      lengthGroupIds,
      diameterGroupIds
    })
    createdSpeciesGroups.push(created)
  }

  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}

async function importRateSheetDefinitions ({
  data, account, accountId, allGrades, allEndUseSorts, actions, results, tabName
}) {
  const definitions = data.rateSheetDefinitions
  if (!definitions || definitions.length === 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('noXDataToImport', { x: tabName }) })
    return
  }

  const existing = await actions.fetchAccountRateSheetDefinitions(accountId)
  if (existing?.length > 0) {
    results.push({ tab: tabName, status: ImportTabStatus.Skipped.value, message: i18n.t('scalingTabSkipped') })
    return
  }

  const serverSpeciesGroups = await actions.fetchAccountSpeciesGroups(accountId)
  const serverLengthGroups = await actions.fetchAccountLengthGroups(accountId)
  const serverDiameterGroups = await actions.fetchAccountDiameterGroups(accountId)

  // Lookup maps
  const speciesGroupByLabel = Object.fromEntries(serverSpeciesGroups.map(sg => [sg.label, sg]))
  const lengthGroupByLabel = Object.fromEntries(serverLengthGroups.map(lg => [lg.label, lg]))
  const diameterGroupByLabel = Object.fromEntries(serverDiameterGroups.map(dg => [dg.label, dg]))
  const gradeByName = Object.fromEntries(allGrades.map(g => [g.name, g]))
  const endUseSortByName = Object.fromEntries(allEndUseSorts.map(e => [e.name, e]))

  if (data.volumePricingMode && data.volumePricingMode !== VolumePricingMode.NotSet.value && account.volumePricingMode === VolumePricingMode.NotSet.value) {
    await actions.patchAccount({
      accountId,
      modifiedAt: account.modifiedAt,
      body: [{ op: 'add', path: '/volumePricingMode', value: data.volumePricingMode }]
    })
  }

  const missing = []
  for (const defn of definitions) {
    for (const vr of (defn.rates || [])) {
      for (const line of (vr.lines || [])) {
        if (line.speciesGroupLabel && !speciesGroupByLabel[line.speciesGroupLabel]) {
          missing.push(`Species Group: ${line.speciesGroupLabel}`)
        }
        if (line.lengthGroupLabel && !lengthGroupByLabel[line.lengthGroupLabel]) {
          missing.push(`Length Group: ${line.lengthGroupLabel}`)
        }
        if (line.diameterGroupLabel && !diameterGroupByLabel[line.diameterGroupLabel]) {
          missing.push(`Diameter Group: ${line.diameterGroupLabel}`)
        }
        if (line.gradeName && !gradeByName[line.gradeName]) {
          missing.push(`Grade: ${line.gradeName}`)
        }
        if (line.endUseSortName && !endUseSortByName[line.endUseSortName]) {
          missing.push(`End Use Sort: ${line.endUseSortName}`)
        }
      }
    }
  }

  if (missing.length > 0) {
    results.push({
      tab: tabName,
      status: ImportTabStatus.Failed.value,
      message: `Missing references: ${[...new Set(missing)].join(', ')}`
    })
    return
  }

  for (const defn of definitions) {
    const rates = (defn.rates || []).map(vr => {
      const lines = (vr.lines || []).map(line => {
        const resolved = { rate: line.rate }
        if (line.speciesGroupLabel) resolved.speciesGroupId = speciesGroupByLabel[line.speciesGroupLabel].speciesGroupId
        if (line.lengthGroupLabel) resolved.lengthGroupId = lengthGroupByLabel[line.lengthGroupLabel].lengthGroupId
        if (line.diameterGroupLabel) resolved.diameterGroupId = diameterGroupByLabel[line.diameterGroupLabel].diameterGroupId
        if (line.gradeName) resolved.gradeId = gradeByName[line.gradeName].gradeId
        if (line.endUseSortName) resolved.endUseSortId = endUseSortByName[line.endUseSortName].endUseSortId
        return resolved
      })
      return { effectiveDate: vr.effectiveDate, rateSheetDefinitionLines: lines }
    })

    await actions.createAccountRateSheetDefinition({
      accountId,
      name: defn.name,
      rates
    })
  }

  results.push({ tab: tabName, status: ImportTabStatus.Success.value, message: i18n.t('scalingTabImported') })
}
